// OverlayTemplates.js

/**
 * Converts a hex color (e.g. "#ff0000") to RGBA with a given alpha (0 - 1).
 */
function hexToRgba(hex, alpha) {
    const cleanHex = hex.replace('#', '');
    const bigint = parseInt(cleanHex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Generates HTML for the "centerLogo" overlay.
 * Accepts an optional { preview: boolean } config to differentiate final vs. preview.
 */
function generateCenterLogoHTML(config, { preview = false } = {}) {
    const rgbaColor = hexToRgba(config.bgColor || '#000000', config.bgAlpha || 0.5);
    const logoSize = config.logoSize || 30;

    if (preview) {
        // Preview mode: Use relative positioning so it fits inside the preview box.
        return `
      <div 
        style="
          position: relative;
          width: 100%;
          height: 300px;
          background-color: ${rgbaColor};
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        "
      >
        <img 
          src="${config.logoUrl || ''}" 
          alt="Center Logo (Preview)" 
          style="
            width: ${logoSize}%;
            height: auto;
          "
        />
      </div>
    `;
    } else {
        // Final mode: position fixed, covers the full screen, etc.
        return `
      <div 
        style="
          pointer-events: none;
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          min-width: 100% !important;
          min-height: 100% !important;
          background-color: ${rgbaColor} !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          overflow: visible !important;
          z-index: 999999 !important;
        "
      >
        <img 
          src="${config.logoUrl || ''}" 
          alt="Center Logo" 
          style="
            width: ${logoSize}%;
            height: auto !important;
            max-width: none !important;
          "
        />
      </div>
    `;
    }
}

/**
 * Generates HTML for the "banner" overlay.
 * Accepts an optional { preview: boolean } config to differentiate final vs. preview.
 */
function generateBannerHTML(config, { preview = false } = {}) {
    if (preview) {
        // Preview mode: a relative container inside the preview box.
        return `
      <div style="
        position: relative;
        width: 100%;
        height: 200px;
        background-color: ${config.bannerBgColor || '#333'};
        display: flex;
        align-items: flex-start;
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
        padding: 1rem;
        overflow: hidden;
      ">
        <div style="margin-right: 1rem;">
          <img 
            src="${config.iconUrl || ''}" 
            alt="Banner Icon (Preview)" 
            style="width: 50px; height: auto;"
          />
        </div>
        <div style="flex: 1; color: ${config.bannerBodyColor || '#ccc'};">
          <h2 style="
            margin: 0; 
            padding: 0; 
            color: ${config.bannerTitleColor || '#fff'};
          ">
            ${config.titleText || ''}
          </h2>
          <p style="margin: 0.25rem 0 0 0; padding: 0;">
            ${config.bodyText || ''}
          </p>
        </div>
      </div>
    `;
    } else {
        // Final mode: position fixed at bottom: 5%, centered horizontally.
        return `
      <div style="
        pointer-events: none;
        position: fixed !important;
        bottom: 5% !important; 
        left: 50% !important;
        transform: translateX(-50%) !important;
        width: ${config.bannerWidth || 100}% !important;
        max-width: none !important;
        padding: 1rem !important;
        background-color: ${config.bannerBgColor || '#333'} !important;
        display: flex !important;
        align-items: flex-start !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3) !important;
        overflow: visible !important;
        z-index: 999999 !important;
      ">
        <div style="margin-right: 1rem !important;">
          <img 
            src="${config.iconUrl || ''}" 
            alt="Banner Icon" 
            style="width: 50px !important; height: auto !important;"
          />
        </div>
        <div style="flex: 1;">
          <h2 style="
            margin: 0 !important; 
            padding: 0 !important; 
            color: ${config.bannerTitleColor || '#fff'} !important;
          ">
            ${config.titleText || ''}
          </h2>
          <p style="
            margin: 0.25rem 0 0 0 !important; 
            padding: 0 !important; 
            color: ${config.bannerBodyColor || '#ccc'} !important;
          ">
            ${config.bodyText || ''}
          </p>
        </div>
      </div>
    `;
    }
}

/**
 * Here we define all our overlay templates. Each has:
 * - id
 * - label (display name)
 * - fields (the user-editable properties)
 * - generateHTML(config, { preview }) => returns HTML string
 */
export const overlayTemplates = [
    {
        id: 'smallLogo',
        label: 'Small Logo',
        fields: [
            {
                name: 'logoUrl',
                label: 'Logo URL',
                type: 'text',
                defaultValue: ''
            },
            {
                name: 'logoSize',
                label: 'Logo Size (%)',
                type: 'range',
                min: 5,
                max: 80,
                step: 5,
                defaultValue: 40
            }
        ],
        generateHTML: (config, { preview = false } = {}) => {
            const size = config.logoSize || 40;

            if (preview) {
                // Preview mode: a simple relative container
                return `
          <div style="
            position: relative;
            width: 100%;
            height: auto;
            display: inline-block;
          ">
            <img 
              src="${config.logoUrl || ''}" 
              alt="Small Logo (Preview)" 
              style="width: ${size}%; height: auto;"
            />
          </div>
        `;
            } else {
                // Final mode: placed absolutely at top-left
                return `
          <div style="
            pointer-events: none;
            position: absolute !important;
            top: 10px !important;
            left: 10px !important;
            z-index: 999999 !important;
          ">
            <img 
              src="${config.logoUrl || ''}" 
              alt="Small Logo" 
              style="width: ${size}%; height: auto;"
            />
          </div>
        `;
            }
        }
    },
    {
        id: 'centerLogo',
        label: 'Center Logo',
        fields: [
            {
                name: 'logoUrl',
                label: 'Logo URL',
                type: 'text',
                defaultValue: ''
            },
            {
                name: 'bgColor',
                label: 'Background color',
                type: 'color',
                defaultValue: '#000000'
            },
            {
                name: 'bgAlpha',
                label: 'Background Opacity (0-1)',
                type: 'range',
                min: 0,
                max: 1,
                step: 0.05,
                defaultValue: 0.5
            },
            {
                name: 'logoSize',
                label: 'Logo Size (%)',
                type: 'range',
                min: 5,
                max: 80,
                step: 5,
                defaultValue: 30
            }
        ],
        generateHTML: generateCenterLogoHTML
    },
    {
        id: 'banner',
        label: 'Banner Overlay',
        fields: [
            {
                name: 'iconUrl',
                label: 'Icon URL',
                type: 'text',
                defaultValue: ''
            },
            {
                name: 'titleText',
                label: 'Title',
                type: 'text',
                defaultValue: 'Next Song'
            },
            {
                name: 'bodyText',
                label: 'Body Text',
                type: 'text',
                defaultValue: 'Here goes a short description...'
            },
            {
                name: 'bannerBgColor',
                label: 'Background Color',
                type: 'color',
                defaultValue: '#333333'
            },
            {
                name: 'bannerTitleColor',
                label: 'Title Color',
                type: 'color',
                defaultValue: '#ffffff'
            },
            {
                name: 'bannerBodyColor',
                label: 'Body Text Color',
                type: 'color',
                defaultValue: '#cccccc'
            },
            {
                name: 'bannerWidth',
                label: 'Banner Width (%)',
                type: 'range',
                min: 10,
                max: 100,
                step: 5,
                defaultValue: 100
            }
        ],
        generateHTML: generateBannerHTML
    }
];